
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserEsignceremonysignee from 'Models/Security/Acl/UserEsignceremonysignee';
import VisitorsEsignceremonysignee from 'Models/Security/Acl/VisitorsEsignceremonysignee';
import { IAcl } from 'Models/Security/IAcl';
import moment from 'moment';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeEnumFetchFunction,
	makeFetchOneToManyFunc,
} from 'Util/EntityUtils';
import * as Validators from 'Validators';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import * as Enums from '../Enums';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface IEsignceremonysigneeEntityAttributes extends IModelAttributes {
	signeestatus: Enums.esignceremonystatus;
	signaturetimestamp: Date;
	signeeip: string;
	signeelocation: string;
	rejectionReason?: string;
	templatecontent: string;

	smsTokenId: string;
	smsToken: Models.SmsTokenEntity | Models.ISmsTokenEntityAttributes;
	esignuserId: string;
	esignuser: Models.EsignuserEntity | Models.IEsignuserEntityAttributes;
	esignworkflowId: string;
	esignworkflow: Models.EsignworkflowEntity | Models.IEsignworkflowEntityAttributes;
	esignceremonywitness?: Models.EsignceremonywitnessEntity |
		Models.IEsignceremonywitnessEntityAttributes;

}

@entity('EsignceremonysigneeEntity', 'ESignCeremonySignee')
export default class EsignceremonysigneeEntity extends Model
	implements IEsignceremonysigneeEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsEsignceremonysignee(),
		new UserEsignceremonysignee(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<EsignceremonysigneeEntity, Enums.esignceremonystatus>()
	@CRUD({
		name: 'SigneeStatus',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.esignceremonystatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.esignceremonystatusOptions),
		displayFunction: (attr: Enums.esignceremonystatus) => Enums.esignceremonystatusOptions[attr],
	})
	public signeestatus: Enums.esignceremonystatus;

	@observable
	@attribute<EsignceremonysigneeEntity, Date>()
	@CRUD({
		name: 'SignatureTimeStamp',
		displayType: 'datetimepicker',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseDate,
	})
	public signaturetimestamp: Date;

	@observable
	@attribute<EsignceremonysigneeEntity, string>()
	@CRUD({
		name: 'SigneeIP',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public signeeip: string;

	@observable
	@attribute<EsignceremonysigneeEntity, string>()
	@CRUD({
		name: 'SigneeLocation',
		displayType: 'textfield',
		order: 40,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public signeelocation: string;

	@observable
	@attribute<EsignceremonysigneeEntity, string>()
	@CRUD({
		name: 'RejectionReason',
		displayType: 'textarea',
		order: 45,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public rejectionReason?: string;

	@observable
	@attribute<EsignceremonysigneeEntity, string>()
	@CRUD({
		name: 'TemplateContent',
		displayType: 'textarea',
		order: 50,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public templatecontent: string;
	

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'SMS Token',
		displayType: 'reference-combobox',
		order: 60,
		referenceTypeFunc: () => Models.SmsTokenEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'smsTokens',
			oppositeEntity: () => Models.SmsTokenEntity,
		}),
		
	})
	public smsTokenId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public smsToken: Models.SmsTokenEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'ESignUser',
		displayType: 'reference-combobox',
		order: 70,
		referenceTypeFunc: () => Models.EsignuserEntity,
		
	})
	public esignuserId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public esignuser: Models.EsignuserEntity;

	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		
		name: 'ESignWorkflow',
		displayType: 'reference-combobox',
		order: 80,
		referenceTypeFunc: () => Models.EsignworkflowEntity,
		
	})
	public esignworkflowId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public esignworkflow: Models.EsignworkflowEntity;

	@observable
	@attribute({ isReference: true, manyReference: false })
	@CRUD({
		
		name: 'ESignCeremonyWitness',
		displayType: 'displayfield',
		order: 90,
		inputProps: {
			displayFunction: (model?: Models.EsignceremonywitnessEntity) => model ? model.getDisplayName() : null,
		},
		
	})
	public esignceremonywitness?: Models.EsignceremonywitnessEntity;



	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IEsignceremonysigneeEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IEsignceremonysigneeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.signeestatus !== undefined) {
				this.signeestatus = attributes.signeestatus;
			}
			if (attributes.signaturetimestamp !== undefined) {
				if (attributes.signaturetimestamp === null) {
					this.signaturetimestamp = attributes.signaturetimestamp;
				} else {
					this.signaturetimestamp = moment(attributes.signaturetimestamp).toDate();
				}
			}
			if (attributes.signeeip !== undefined) {
				this.signeeip = attributes.signeeip;
			}
			if (attributes.signeelocation !== undefined) {
				this.signeelocation = attributes.signeelocation;
			}
			if (attributes.templatecontent !== undefined) {
				this.templatecontent = attributes.templatecontent;
			}
			if (attributes.smsTokenId !== undefined) {
				this.smsTokenId = attributes.smsTokenId;
			}
			if (attributes.smsToken !== undefined) {
				if (attributes.smsToken === null) {
					this.smsToken = attributes.smsToken;
				} else if (attributes.smsToken instanceof Models.SmsTokenEntity) {
					this.smsToken = attributes.smsToken;
					this.smsTokenId = attributes.smsToken.id;
				} else {
					this.smsToken = new Models.SmsTokenEntity(attributes.smsToken);
					this.smsTokenId = this.smsToken.id;
				}
			}
			if (attributes.esignuserId !== undefined) {
				this.esignuserId = attributes.esignuserId;
			}
			if (attributes.esignuser !== undefined) {
				if (attributes.esignuser === null) {
					this.esignuser = attributes.esignuser;
				} else if (attributes.esignuser instanceof Models.EsignuserEntity) {
					this.esignuser = attributes.esignuser;
					this.esignuserId = attributes.esignuser.id;
				} else {
					this.esignuser = new Models.EsignuserEntity(attributes.esignuser);
					this.esignuserId = this.esignuser.id;
				}
			}
			if (attributes.esignworkflowId !== undefined) {
				this.esignworkflowId = attributes.esignworkflowId;
			}
			if (attributes.esignworkflow !== undefined) {
				if (attributes.esignworkflow === null) {
					this.esignworkflow = attributes.esignworkflow;
				} else if (attributes.esignworkflow instanceof Models.EsignworkflowEntity) {
					this.esignworkflow = attributes.esignworkflow;
					this.esignworkflowId = attributes.esignworkflow.id;
				} else {
					this.esignworkflow = new Models.EsignworkflowEntity(attributes.esignworkflow);
					this.esignworkflowId = this.esignworkflow.id;
				}
			}
			if (attributes.esignceremonywitness !== undefined) {
				if (attributes.esignceremonywitness === null) {
					this.esignceremonywitness = attributes.esignceremonywitness;
				} else if (attributes.esignceremonywitness instanceof Models.EsignceremonywitnessEntity) {
					this.esignceremonywitness = attributes.esignceremonywitness;
				} else {
					this.esignceremonywitness = new Models.EsignceremonywitnessEntity(attributes.esignceremonywitness);
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		smsToken {
			${Models.SmsTokenEntity.getAttributes().join('\n')}
		}
		esignuser {
			${Models.EsignuserEntity.getAttributes().join('\n')}
		}
		esignworkflow {
			${Models.EsignworkflowEntity.getAttributes().join('\n')}
		}
		esignceremonywitness {
			${Models.EsignceremonywitnessEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'smsToken',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(EsignceremonysigneeEntity.prototype, 'created');
CRUD(modifiedAttr)(EsignceremonysigneeEntity.prototype, 'modified');
